import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from 'devextreme-react/button';
import DataGrid, { Column, Item, Toolbar, type DataGridRef } from 'devextreme-react/data-grid';
import {
  GridDetailPopup,
  OPS_SITE_GRID_PROPS,
  OpsGridToolbarItems,
  OpsSiteGridChrome,
  opsGridDetailPopupSizeKey
} from '../opsDataGrid';
import PieChart, { Legend, Series, Label as ChartLabel } from 'devextreme-react/pie-chart';
import Chart, { ArgumentAxis, Series as BarSeries, ValueAxis } from 'devextreme-react/chart';
import Popup from 'devextreme-react/popup';
import ScrollView from 'devextreme-react/scroll-view';
import SelectBox from 'devextreme-react/select-box';
import TabPanel, { Item as TabItem } from 'devextreme-react/tab-panel';
import TextArea from 'devextreme-react/text-area';
import TextBox from 'devextreme-react/text-box';
import { showDeleteConfirmDialog } from '../dialogs/confirmDialog';
import { showErrorToast, showSuccessToast } from '../notifications/toast';
import {
  SiteSupportAdminRowActions,
  siteSupportAdminActionsColumnWidth
} from './SiteSupportAdminRowActions';
import { FeedbackDiscussionChat } from './FeedbackDiscussionChat';
import { useFeedbackDiscussion } from './useFeedbackDiscussion';
import type { CommentDetail, CommentListItem, FeedbackMetrics, SiteFeedbackApi } from './types';

const FEEDBACK_GRID_STATE_KEY = 'site-support-feedback-grid';
const FALLBACK_STATUSES = ['Open', 'Closed', 'Planned', 'In Progress', 'Done'];
const FALLBACK_PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];
const FALLBACK_WORKLOADS = ['XS', 'S', 'M', 'L', 'XL'];

function formatStarRating(starRating: number | null | undefined): string {
  if (starRating == null) return '—';
  const safe = Math.max(0, Math.min(5, Math.round(starRating)));
  return `${'★'.repeat(safe)}${'☆'.repeat(5 - safe)} (${safe}/5)`;
}

export interface AdminFeedbackPageProps {
  api: SiteFeedbackApi;
  canManage?: boolean;
  currentUserId?: string;
  currentUserName?: string;
}

export function AdminFeedbackPage({
  api,
  canManage = false,
  currentUserId = 'admin',
  currentUserName
}: AdminFeedbackPageProps) {
  const gridRef = useRef<DataGridRef>(null);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState<CommentListItem[]>([]);
  const [metrics, setMetrics] = useState<FeedbackMetrics | null>(null);
  const [detail, setDetail] = useState<CommentDetail | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editStatus, setEditStatus] = useState('Open');
  const [editPriority, setEditPriority] = useState('Medium');
  const [editSubject, setEditSubject] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [editTicket, setEditTicket] = useState('');
  const [editWorkload, setEditWorkload] = useState('');
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [statuses, setStatuses] = useState(FALLBACK_STATUSES);
  const [priorities, setPriorities] = useState(FALLBACK_PRIORITIES);
  const [workloads, setWorkloads] = useState(FALLBACK_WORKLOADS);
  const discussion = useFeedbackDiscussion(
    api,
    'feedback',
    detail?.publicId ?? null,
    viewOpen && Boolean(detail)
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, m] = await Promise.all([api.listComments(0, 500), api.getFeedbackMetrics()]);
      setComments(list.items);
      setMetrics(m);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load feedback.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void Promise.all([
      api.listActiveMeta('FeedbackStatus'),
      api.listActiveMeta('Priority'),
      api.listActiveMeta('Workload')
    ])
      .then(([s, p, w]) => {
        if (s.length) setStatuses(s.map((r) => r.name));
        if (p.length) setPriorities(p.map((r) => r.name));
        if (w.length) setWorkloads(w.map((r) => r.name));
      })
      .catch(() => undefined);
  }, [api]);

  const ratingData = useMemo(
    () => metrics?.ratingDistribution.map((s) => ({ label: s.label, value: s.count })) ?? [],
    [metrics]
  );
  const typeData = useMemo(
    () => metrics?.byType?.map((s) => ({ label: s.label, value: s.count })) ?? [],
    [metrics]
  );
  const overTimeData = useMemo(
    () => metrics?.overTime.map((d) => ({ date: d.date, count: d.count })) ?? [],
    [metrics]
  );

  const openView = useCallback(
    async (commentId: string) => {
      try {
        setDetail(await api.getComment(commentId));
        setViewOpen(true);
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : 'Failed to load feedback.');
      }
    },
    [api]
  );

  const openEdit = useCallback(async () => {
    if (!detail || !canManage) return;
    setEditStatus(detail.status);
    setEditPriority(detail.priority);
    setEditSubject(detail.subject);
    setEditMessage(detail.message);
    setEditTicket(detail.ticketUrl ?? '');
    setEditWorkload(detail.workload ?? '');
    setEditStart(detail.startAt ? detail.startAt.slice(0, 10) : '');
    setEditEnd(detail.endAt ? detail.endAt.slice(0, 10) : '');
    setViewOpen(false);
    setEditOpen(true);
  }, [canManage, detail]);

  const saveEdit = useCallback(async () => {
    if (!detail || !canManage) return;
    try {
      await api.updateComment(detail.id, {
        status: editStatus,
        priority: editPriority,
        subject: editSubject,
        message: editMessage,
        ticketUrl: editTicket || undefined,
        workload: editWorkload || null,
        startAt: editStart ? new Date(`${editStart}T00:00:00`).toISOString() : null,
        endAt: editEnd ? new Date(`${editEnd}T23:59:59`).toISOString() : null
      });
      showSuccessToast('Feedback ticket updated.');
      setEditOpen(false);
      void load();
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Update failed.');
    }
  }, [
    api,
    canManage,
    detail,
    editEnd,
    editMessage,
    editPriority,
    editStart,
    editStatus,
    editSubject,
    editTicket,
    editWorkload,
    load
  ]);

  const claim = useCallback(async () => {
    if (!detail || !canManage) return;
    try {
      await api.claimComment(detail.id, Boolean(detail.assigneeUserId));
      showSuccessToast(detail.assigneeUserId ? 'Reclaimed.' : 'Claimed.');
      setDetail(await api.getComment(detail.id));
      void load();
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Claim failed.');
    }
  }, [api, canManage, detail, load]);

  const deleteComment = useCallback(
    async (commentId: string) => {
      if (!canManage) return;
      if (!(await showDeleteConfirmDialog('Delete this feedback ticket? This cannot be undone.'))) return;

      try {
        await api.deleteComment(commentId);
        showSuccessToast('Feedback deleted.');
        if (detail?.id === commentId) {
          setViewOpen(false);
          setDetail(null);
        }
        void load();
      } catch (e) {
        showErrorToast(e instanceof Error ? e.message : 'Failed to delete feedback.');
      }
    },
    [api, canManage, detail?.id, load]
  );

  return (
    <>
      <ScrollView>
        <div className="ops-page site-support-admin">
          <h2 className="dashboard-title">Feedback</h2>

          {metrics ? (
            <div className="site-support-summary-card">
              <div className="site-support-summary-card__rating">{metrics.averageRating.toFixed(1)}</div>
              <div className="site-support-summary-card__meta">Based on {metrics.totalResponses} responses</div>
            </div>
          ) : null}

          <div className="site-support-charts">
            <div className="site-support-chart-card">
              <div className="site-support-chart-card__title">Rating distribution</div>
              <PieChart dataSource={ratingData} type="doughnut">
                <Series argumentField="label" valueField="value">
                  <ChartLabel visible />
                </Series>
                <Legend visible />
              </PieChart>
            </div>
            <div className="site-support-chart-card">
              <div className="site-support-chart-card__title">Feedback by type</div>
              <PieChart dataSource={typeData} type="doughnut">
                <Series argumentField="label" valueField="value">
                  <ChartLabel visible />
                </Series>
                <Legend visible />
              </PieChart>
            </div>
            <div className="site-support-chart-card">
              <div className="site-support-chart-card__title">Feedback over time</div>
              <Chart dataSource={overTimeData}>
                <ArgumentAxis />
                <ValueAxis />
                <BarSeries valueField="count" argumentField="date" type="bar" />
              </Chart>
            </div>
          </div>

          <h3 className="site-support-section-title">Feedback tickets</h3>
          <DataGrid
            ref={gridRef}
            dataSource={comments}
            keyExpr="id"
            showBorders
            columnAutoWidth
            columnHidingEnabled
            allowColumnResizing
            allowColumnReordering
            columnResizingMode="widget"
            {...OPS_SITE_GRID_PROPS}
          >
            <OpsSiteGridChrome
              stateStorageKey={FEEDBACK_GRID_STATE_KEY}
              searchPlaceholder="Search feedback…"
            />
            <Toolbar>
              <OpsGridToolbarItems
                gridRef={gridRef}
                loading={loading}
                onRefresh={() => void load()}
                refreshHint="Refresh feedback"
              />
              <Item name="searchPanel" location="after" />
            </Toolbar>
            <Column dataField="publicId" caption="ID" width={130} />
            <Column dataField="submittedAt" caption="Date" dataType="datetime" width={160} />
            <Column dataField="feedbackType" caption="Type" width={100} />
            <Column dataField="subject" caption="Title" />
            <Column dataField="status" caption="Status" width={110} />
            <Column dataField="priority" caption="Priority" width={90} />
            <Column dataField="assigneeDisplayName" caption="Owner" width={140} />
            <Column dataField="starRating" caption="Rating" width={80} />
            <Column
              caption=""
              width={siteSupportAdminActionsColumnWidth(canManage ? 3 : 1)}
              minWidth={48}
              allowSorting={false}
              allowFiltering={false}
              allowHiding={false}
              cellRender={({ data }: { data: CommentListItem }) => (
                <SiteSupportAdminRowActions
                  viewHint="View feedback"
                  onView={() => void openView(data.id)}
                  onDelete={canManage ? () => void deleteComment(data.id) : undefined}
                  deleteHint="Delete feedback"
                />
              )}
            />
          </DataGrid>
        </div>
      </ScrollView>

      <GridDetailPopup
        visible={viewOpen}
        onClose={() => setViewOpen(false)}
        title={detail ? `Feedback ${detail.publicId}` : 'Feedback'}
        width={760}
        maxBodyHeight={720}
        sizeStorageKey={opsGridDetailPopupSizeKey(FEEDBACK_GRID_STATE_KEY)}
        primaryActions={
          detail && canManage
            ? [
                {
                  text: detail.assigneeUserId ? 'Reclaim' : 'Claim',
                  onClick: () => void claim()
                },
                {
                  text: 'Edit',
                  type: 'default',
                  stylingMode: 'contained',
                  onClick: () => void openEdit()
                },
                {
                  text: 'Delete',
                  type: 'danger',
                  onClick: () => void deleteComment(detail.id)
                }
              ]
            : undefined
        }
      >
        {detail ? (
          <TabPanel
            className="ops-grid-detail-tab-panel site-support-ticket-tabs"
            deferRendering={false}
            animationEnabled={false}
            swipeEnabled={false}
            showNavButtons={false}
          >
            <TabItem title="Details">
              <div className="site-support-popup-body site-support-detail">
                <dl>
                  <dt>Title</dt>
                  <dd>{detail.subject}</dd>
                  <dt>Type</dt>
                  <dd>{detail.feedbackType}</dd>
                  <dt>Status</dt>
                  <dd>{detail.status}</dd>
                  <dt>Priority</dt>
                  <dd>{detail.priority}</dd>
                  <dt>Rating</dt>
                  <dd className="site-support-detail__rating">{formatStarRating(detail.starRating)}</dd>
                  <dt>Owner</dt>
                  <dd>{detail.assigneeDisplayName ?? detail.assigneeUserId ?? 'Unclaimed'}</dd>
                  <dt>Workload</dt>
                  <dd>{detail.workload ?? '—'}</dd>
                  <dt>Ticket URL</dt>
                  <dd>{detail.ticketUrl ?? '—'}</dd>
                  <dt>Submitted by</dt>
                  <dd>{detail.submittedByDisplayName ?? detail.submittedBy}</dd>
                  <dt>Watchers</dt>
                  <dd>
                    {detail.watchers?.length
                      ? detail.watchers.map((w) => w.displayName || w.userId).join(', ')
                      : '—'}
                  </dd>
                </dl>
                <div className="site-support-detail__message">{detail.message}</div>
              </div>
            </TabItem>
            <TabItem title="Discussion">
              <FeedbackDiscussionChat
                title=""
                messages={discussion.messages}
                currentUserId={currentUserId}
                currentUserName={currentUserName ?? currentUserId}
                loading={discussion.loading}
                onMessageEntered={discussion.post}
                onMessageDeleted={discussion.remove}
              />
            </TabItem>
          </TabPanel>
        ) : (
          <div className="site-support-popup-body">Loading…</div>
        )}
      </GridDetailPopup>

      <Popup
        visible={editOpen}
        onHiding={() => setEditOpen(false)}
        showTitle
        title="Edit Feedback Ticket"
        width={520}
        height="auto"
        maxHeight="90vh"
        hideOnOutsideClick
        contentRender={() => (
          <div className="site-support-popup-body site-support-form">
            <label className="site-support-label">Status</label>
            <SelectBox items={statuses} value={editStatus} onValueChanged={(e) => setEditStatus(String(e.value ?? ''))} />
            <label className="site-support-label">Priority</label>
            <SelectBox
              items={priorities}
              value={editPriority}
              onValueChanged={(e) => setEditPriority(String(e.value ?? ''))}
            />
            <label className="site-support-label">Workload</label>
            <SelectBox
              items={['', ...workloads]}
              value={editWorkload}
              onValueChanged={(e) => setEditWorkload(String(e.value ?? ''))}
            />
            <label className="site-support-label">Title</label>
            <TextBox value={editSubject} onValueChanged={(e) => setEditSubject(String(e.value ?? ''))} />
            <label className="site-support-label">Description</label>
            <TextArea value={editMessage} height={120} onValueChanged={(e) => setEditMessage(String(e.value ?? ''))} />
            <label className="site-support-label">Start date</label>
            <TextBox value={editStart} onValueChanged={(e) => setEditStart(String(e.value ?? ''))} placeholder="yyyy-MM-dd" />
            <label className="site-support-label">End date</label>
            <TextBox value={editEnd} onValueChanged={(e) => setEditEnd(String(e.value ?? ''))} placeholder="yyyy-MM-dd" />
            <label className="site-support-label">Ticket URL</label>
            <TextBox value={editTicket} onValueChanged={(e) => setEditTicket(String(e.value ?? ''))} />
            <div className="site-support-form-actions">
              <Button text="Cancel" stylingMode="outlined" onClick={() => setEditOpen(false)} />
              <Button text="Update" type="default" onClick={() => void saveEdit()} />
            </div>
          </div>
        )}
      />
    </>
  );
}
